import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FormField from './FormField';

interface AuthFormProps {
   isLogin: boolean;
   email: string;
   password: string;
   confirmPassword: string;
   error: string;
   notice: string;
   isLoading: boolean;
   onSubmit: (e: React.FormEvent) => void;
   onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
   onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
   onConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
   onToggle: () => void;
}

function AuthForm({
   isLogin,
   email,
   password,
   confirmPassword,
   error,
   notice,
   isLoading,
   onSubmit,
   onEmailChange,
   onPasswordChange,
   onConfirmPasswordChange,
   onToggle,
}: AuthFormProps) {
   return (
      <motion.div
         initial={{ opacity: 0 }}
         animate={{ opacity: 1 }}
         transition={{ duration: 0.5, delay: 0.2 }}
         className="glass rounded-lg p-8 border border-slate/50"
      >
         <h2 className="text-2xl font-display text-pearl mb-6">
            {isLogin ? 'WELCOME BACK' : 'JOIN QUILL STUDIO'}
         </h2>

         <form onSubmit={onSubmit} className="space-y-4">
            <FormField
               label="EMAIL ADDRESS"
               type="email"
               value={email}
               onChange={onEmailChange}
               placeholder="Email"
               required
            />

            <FormField
               label="PASSWORD"
               type="password"
               value={password}
               onChange={onPasswordChange}
               placeholder="Password"
               required
            />

            {!isLogin && (
               <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
               >
                  <FormField
                     label="CONFIRM PASSWORD"
                     type="password"
                     value={confirmPassword}
                     onChange={onConfirmPasswordChange}
                     placeholder="Password"
                     required={!isLogin}
                  />
               </motion.div>
            )}

            <AnimatePresence>
               {error && (
                  <motion.div
                     initial={{ opacity: 0, y: -10 }}
                     animate={{ opacity: 1, y: 0 }}
                     exit={{ opacity: 0, y: -10 }}
                     transition={{ duration: 0.2 }}
                     className="p-3 bg-blood/20 border border-blood/50 rounded text-rose text-xs font-mono"
                  >
                     {error}
                  </motion.div>
               )}
            </AnimatePresence>

            {notice && (
               <div className="p-3 bg-emerald/20 border border-emerald/40 rounded text-emerald font-mono text-xs">
                  {notice}
               </div>
            )}

            <motion.button
               type="submit"
               disabled={isLoading}
               whileHover={{ scale: 1.02 }}
               whileTap={{ scale: 0.98 }}
               className={`w-full py-3 bg-amethyst hover:bg-rose rounded font-display text-xl text-white shadow-lg hover:shadow-xl transition-colors ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
               }`}
            >
               {isLoading ? 'PROCESSING...' : isLogin ? 'ENTER THE STUDIO' : 'CREATE ACCOUNT'}
            </motion.button>
         </form>

         <div className="mt-6 text-center">
            <button
               onClick={onToggle}
               className="text-silver hover:text-pearl font-mono text-xs transition-colors"
            >
               {isLogin ? "DON'T HAVE AN ACCOUNT? SIGN UP" : 'ALREADY HAVE AN ACCOUNT? LOG IN'}
            </button>
         </div>
      </motion.div>
   );
}

export default AuthForm;
